import React, { useEffect, useState } from "react";
import SplashScreen from "@/components/SplashScreen";
import OfflineOverlay from "@/components/OfflineOverlay";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { NotificationProvider } from "@/context/NotificationContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import Landing from "@/pages/Landing";
import Catalog from "@/pages/Catalog";
import ProductDetail from "@/pages/ProductDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import SellListing from "@/pages/SellListing";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Messages from "@/pages/Messages";
import Wishlist from "@/pages/Wishlist";
import SellerProfile from "@/pages/SellerProfile";
import Manifesto from "@/pages/Manifesto";
import Notifications from "@/pages/Notifications";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import { AnimatePresence, motion } from "framer-motion";
import { isNativeApp } from "@/lib/utils";
import { api } from "@/lib/api";
import OrderDetail from "@/pages/OrderDetail";
import Admin from "@/pages/Admin";
import IOSInstallBanner from "@/components/IOSInstallBanner";
import PlayStoreBanner from "@/components/PlayStoreBanner";
import Auctions from "@/pages/Auctions";
import AuctionDetail from "@/pages/AuctionDetail";
import Logistics from "@/pages/Logistics";
import LegalNotice from "@/pages/LegalNotice";
import Privacy from "@/pages/Privacy";
import TermsOfSale from "@/pages/TermsOfSale";
import TermsOfUse from "@/pages/TermsOfUse";
import Cookies from "@/pages/Cookies";
import AccountDeletion from "@/pages/AccountDeletion";
import CookieBanner from "@/components/CookieBanner";
import AgentDashboard from "@/pages/AgentDashboard";
import LiveSales from "@/pages/LiveSales";
import LiveSessionRoom from "@/pages/LiveSessionRoom";
import BecomeAgent from "@/pages/BecomeAgent";
import FripriPro from "@/pages/FripriPro";

function AppShell() {
  const location = useLocation();
  const native = isNativeApp();
  const [showSplash, setShowSplash] = useState(native);
  useEffect(() => {
    if (!native) return;
    document.body.classList.add("native-app");
    try {
      const last = localStorage.getItem("fripri_app_ping");
      if (last && Date.now() - parseInt(last, 10) < 86400000) return;
      let dev = localStorage.getItem("fripri_device_id");
      if (!dev) {
        dev = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()).slice(2);
        localStorage.setItem("fripri_device_id", dev);
      }
      const platform = /android/i.test(navigator.userAgent) ? "android" : "ios";
      api.post("/app/ping", { device: dev, platform })
        .then(() => localStorage.setItem("fripri_app_ping", String(Date.now())))
        .catch(() => {});
    } catch {}
  }, [native]);
  // Detect OAuth callback in URL hash synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const showHeader = !["/login", "/register"].includes(location.pathname);
  return (
    <>
      {showSplash && <SplashScreen onDone={() => setShowSplash(false)} />}
      <OfflineOverlay />
      {showHeader && <Header />}
      <main>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={native ? location.pathname : "static"}
            initial={native ? { opacity: 0, y: 10 } : false}
            animate={{ opacity: 1, y: 0 }}
            exit={native ? { opacity: 0, y: -6 } : undefined}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
        <Routes location={location}>
          <Route path="/" element={<Landing />} />
          <Route path="/catalogue" element={<Catalog />} />
          <Route path="/produit/:id" element={<ProductDetail />} />
          <Route path="/vendeur/:id" element={<SellerProfile />} />
          <Route path="/boutique/:slug" element={<SellerProfile />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/commandes" element={<Dashboard />} />
          <Route path="/vendre" element={<SellListing />} />
          <Route path="/panier" element={<Cart />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/favoris" element={<Wishlist />} />
          <Route path="/manifeste" element={<Manifesto />} />
          <Route path="/encheres" element={<Auctions />} />
          <Route path="/encheres/:id" element={<AuctionDetail />} />
          <Route path="/agent" element={<AgentDashboard />} />
          <Route path="/brocante-live" element={<LiveSales />} />
          <Route path="/brocante-live/:id" element={<LiveSessionRoom />} />
          <Route path="/devenir-agent" element={<BecomeAgent />} />
          <Route path="/pro" element={<FripriPro />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/commandes/:id" element={<OrderDetail />} />
          <Route path="/admin/*" element={<Admin />} />
          <Route path="/dirlog" element={<Logistics />} />
          <Route path="/mentions-legales" element={<LegalNotice />} />
          <Route path="/legal" element={<LegalNotice />} />
          <Route path="/confidentialite" element={<Privacy />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cgv" element={<TermsOfSale />} />
          <Route path="/terms-of-sale" element={<TermsOfSale />} />
          <Route path="/cgu" element={<TermsOfUse />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/supprimer-mon-compte" element={<AccountDeletion />} />
          <Route path="/account-deletion" element={<AccountDeletion />} />
          <Route path="/delete-account" element={<AccountDeletion />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/google" element={<GoogleCallback />} />
        </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
      {showHeader && <Footer />}
      {showHeader && <BottomNav />}
      <IOSInstallBanner />
      <PlayStoreBanner />
      <CookieBanner />
    </>
  );
}

function App() {
  // Register service worker once
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/service-worker.js").catch(() => {});
    }
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <AppShell />
            <Toaster richColors position="top-center" toastOptions={{ style: { borderRadius: 0, border: "2px solid #000", fontFamily: "Geist" } }} />
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
